import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import TreatmentErrorView from 'components/automatic-billiing/segments/TreatmentErrorView';
import {
  ConsultationsConsumerRenderProps,
  withConsultationsContext
} from 'components/consultations/ConsultationsContext';
import UpsertGuarantorForm from 'components/guarantors/UpsertGuarantorForm';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import UpsertPatient from 'components/patient/upsert/UpsertPatient';
import { EditTreatmentValues, TreatmentMode } from 'components/treatments/TreatmentsContext';
import TreatmentsForm from 'components/treatments/TreatmentsForm';
import _ from 'lodash';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Header, Icon, Modal, Popup } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import { findGuarantorById } from 'services/guarantorServices';
import axios from 'services/http';
import { getAllLanguages } from 'services/languageServices';
import { findPatientById } from 'services/patientServices';
import { getTreatmentById, getTreatmentOverview } from 'services/treatmentServices';
import styled from 'styled-components';
import { BillStatus } from 'ts-types/api.enums';
import {
  CountryDto,
  LanguageDto,
  PatientDto,
  TariffDto,
  TreatmentOverviewConsultationDto,
  TreatmentOverviewDto,
  TreatmentOverviewSearchRequest,
  TreatmentOverviewSearchResponse,
  UpsertGuarantorDto,
  UpsertPatientDto
} from 'ts-types/api.types';
import { numberFormatter } from 'utils/fomattingUtils';
import { noop } from 'utils/functionUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;


const StyledTreatmentsTable = styled(StyledTable)`

  .ReactVirtualized__Table__headerColumn:first-of-type,
  .ReactVirtualized__Table__rowColumn:first-of-type {
    margin-left: unset;
  }

  .edit-icon {
    padding-left: 8px;
    padding-right: 8px;
  }

  .edit-guarantor-icon {
    padding-right: 8px;
  }

  .status-icons-container {
    text-align: center;
  }
`;

const TableContainer = styled.div`
  height: 90%;
  min-height: 300px;
`;

const StyledForm = styled.form`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;
`;

const StyledFormContent = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }

  .header-description {
    margin-bottom: 0.1rem;

    & .icon {
      font-size: 1em;
    }
  }

  .header-data {
    margin-top: 0.2rem;
    font-weight: 300;
  }
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

const StyledNumberCell = styled.div`
  text-align: right;
  padding-right: 1rem;
`;

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    RouteComponentProps,
    ConsultationsConsumerRenderProps,
    MandateConsumerRenderProps {

  searchTreatments: (exportToFile?: boolean) => void,
  onFilterTreatments: (searchResponse: TreatmentOverviewSearchResponse) => void,
  onTreatmentsLoadingChange: (treatmentsLoading: boolean) => void,
  treatments: TreatmentOverviewDto[],
  treatmentsLoading: boolean,
  tariffs: TariffDto[],
  setErrorMessage: (errorMessage?: string) => void
}

const TreatmentOverviewTablesSegment = (props: Props) => {

  const {
    treatments,
    currentConsultations,
    treatmentsLoading,
    onFilterTreatments,
    searchTreatments,
    currentMandates,
    getAllMandates,
    setErrorMessage,
    getMessage,
    getMessageWithNamedParams
  } = props;

  const cancelTokenSource = axios.CancelToken.source();

  const [selectedTreatmentIx, setSelectedTreatmentIx] = useState(treatments && treatments.length > 0 ? 0 : -1);
  const [selectedConsultationIx, setSelectedConsultationIx] =
      useState(treatments && treatments.length > 0 && treatments[0].consultations.length > 0 ? 0 : -1);

  const [countries, setCountries] = useState([] as CountryDto[]);
  const [languages, setLanguages] = useState([] as LanguageDto[]);

  const [treatment, setTreatment] = useState(undefined as EditTreatmentValues | undefined);
  const [editTreatmentModalOpen, setEditTreatmentModalOpen] = useState(false);

  const [patient, setPatient] = useState(undefined as UpsertPatientDto | undefined);
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);

  const [guarantor, setGuarantor] = useState(undefined as UpsertGuarantorDto | undefined);
  const [editGuarantorModalOpen, setEditGuarantorModalOpen] = useState(false);

  useEffect(() => {
    getAllCountries(cancelTokenSource).then(response => {
      setCountries(response);
    }).catch(noop);

    getAllLanguages(cancelTokenSource).then(response => {
      setLanguages(response);
    }).catch(noop);

    setSelectedTreatmentIx(treatments && treatments.length > 0 ? 0 : -1);
    setSelectedConsultationIx(
        treatments && treatments.length > 0 && treatments[0].consultations.length > 0 ? 0 : -1
    );
  }, [])

  const getModifiedTreatments = (modifiedTreatmentIds: number[]) => {
    if (modifiedTreatmentIds.length) {
      const searchRequest: Partial<TreatmentOverviewSearchRequest> = {
        modifiedTreatmentIds
      };
      getTreatmentOverview(searchRequest, cancelTokenSource)
          .then(response => {
            const responseTreatments = _.keyBy(response.treatments, "id")
            const modifiedTreatments = treatments.map(t => {
              if (responseTreatments[t.id]) {
                return responseTreatments[t.id];
              }
              return t;
            });
            onFilterTreatments({treatments: modifiedTreatments});
          })
          .catch((e) => {
            setErrorMessage(getMessage("error.general"));
          })
          .finally(() => {
          });
    }
  };

  const openEditTreatmentModal = (treatmentId: number, mode?: TreatmentMode) => {
    if (treatmentId) {
      getTreatmentById(treatmentId, cancelTokenSource)
          .then(response => {
            const upsertTreatment: EditTreatmentValues = {
              ...response,
              id: treatmentId,
              readOnly: mode === TreatmentMode.DETAILS
            }
            setTreatment(upsertTreatment)
            toggleEditTreatmentModal();
          })
          .catch((e) => setEditTreatmentModalOpen(false))
    }
  }

  const onTreatmentSave = () => {
    if (treatment && treatment.id) {
      const modifiedTreatmentIds: number[] = [treatment.id];
      getModifiedTreatments(modifiedTreatmentIds);
    }
  }

  const toggleEditTreatmentModal = () => {
    if (editTreatmentModalOpen) {
      onTreatmentSave();
    }
    setEditTreatmentModalOpen(!editTreatmentModalOpen);
  }

  const openEditPatientModal = (patientId?: number) => {
    if (patientId) {
      findPatientById(cancelTokenSource, patientId)
          .then(response => {
            setPatient(response);
            toggleEditPatientModal();
          })
          .catch(noop)
    }
  };

  const toggleEditPatientModal = () => {
    if (editTreatmentModalOpen) {
      searchTreatments();
    }
    setEditPatientModalOpen(!editPatientModalOpen);
  }

  const onPatientSave = (savedPatient: PatientDto) => {
    if (savedPatient) {
      const patientTreatmentIds = treatments
          .filter(t => t.patientId === savedPatient.id)
          .map(t => t.id);
      getModifiedTreatments(patientTreatmentIds);
    }
    toggleEditPatientModal();
  };

  const openEditGuarantorModal = (id: number | undefined) => {
    if (id) {
      findGuarantorById(id, cancelTokenSource).then(response => {
        showGuarantorModal(id, response);
      }).catch(noop);
    }
  };

  const showGuarantorModal = (id?: number, guarantor?: UpsertGuarantorDto) => {
    setEditGuarantorModalOpen(true);
    setGuarantor(guarantor);
  };

  const hideGuarantorModal = () => {
    setEditGuarantorModalOpen(false);
    setGuarantor(undefined);
  };

  const onGuarantorSave = () => {
    if (selectedTreatmentIx && treatments[selectedTreatmentIx]) {
      const modifiedTreatment = treatments[selectedTreatmentIx];
      const guarantorTreatmentIds = treatments
          .filter(t => t.guarantorId === modifiedTreatment.guarantorId)
          .map(t => t.id);
      getModifiedTreatments(guarantorTreatmentIds);
    }
    hideGuarantorModal();
  };

  const highlightTreatment = (treatments: any, index: number) => {

    setSelectedTreatmentIx(index);
    setSelectedConsultationIx(treatments[index].consultations.length > 0 ? 0 : -1)
  };

  const treatmentDateCellRenderer = (data: any) => <div>{moment(data.rowData.treatmentDate).format("DD.MM.YYYY")}</div>;
  const amountCellRenderer = (data: any) => <StyledCell>{numberFormatter.format(data.rowData.amount)}</StyledCell>;
  const consultationAmountCellRenderer = (data: any) =>
      <StyledCell>{numberFormatter.format(data.rowData.consultationAmount)}</StyledCell>;

  const treatmentsErrorsCellRenderer = (treatments: Array<TreatmentOverviewDto>) => (data: any) => {
    const treatmentIx = data.rowIndex;

    const treatment = treatments[treatmentIx];
    const hasErrors = treatment.errors.length;

    const trigger = <Icon name="warning sign" color="red"/>

    return (
        <React.Fragment>
          {hasErrors
              ?
              <>
                <Popup
                    trigger={trigger}
                    popperDependencies={[]}
                    popperModifiers={{
                      preventOverflow: {
                        boundary: document.getElementById("treatmentsMainTable")
                      }
                    }}
                    content={
                      <TreatmentErrorView errors={treatment.errors}/>
                    }
                    flowing
                    on="hover"
                    size="large"
                    position="right center"
                />
              </>
              : <></>
          }

        </React.Fragment>
    );
  };

  const treatmentBookingIdProductCellRenderer = ({rowData}: any) => {
    let bookingIdProduct = "";
    if (rowData.interventionBookingId) {
      bookingIdProduct = rowData.interventionBookingId;
    }

    if (rowData.productId) {
      bookingIdProduct += ` (${rowData.productId})`;
    }

    return bookingIdProduct;
  }

  const treatmentCellRenderer = (treatments: Array<TreatmentOverviewDto>) => (data: any) => {
    const {rowData, cellData} = data;
    const {getMessage} = props;

    const treatmentIx = data.rowIndex;

    const treatment = treatments[treatmentIx];
    const hasErrors = treatment.errors.length;

    const trigger = <Icon name="warning sign" color="red"/>

    let icons = [];

    if (rowData.interrupted) {
      icons.push(
          <Fragment>
            <Icon.Group size="large">
              <Icon name="treatment" title={getMessage("treatmentOverview.interrupted")}/>
              <Icon corner="bottom right" name="times" color="red"/>
            </Icon.Group>&nbsp;
          </Fragment>
      )
    }

    if (cellData) {
      icons.push(<Icon name={"key"} title={getMessage("enum.treatmentStatus.CLOSED")}/>);
    }

    return (
          <div className="status-icons-container">
            {hasErrors
                ?
                <>
                  <Popup
                      trigger={trigger}
                      popperDependencies={[]}
                      popperModifiers={{
                        preventOverflow: {
                          boundary: document.getElementById("treatmentsMainTable")
                        }
                      }}
                      content={
                        <TreatmentErrorView errors={treatment.errors}/>
                      }
                      flowing
                      on="hover"
                      size="large"
                      position="right center"
                  />
                </>
                : <></>
            }
            {icons}
          </div>
    );
  };

  const treatmentIdCellRenderer = (data: any) => {
    const treatmentId = data.cellData;
    const {getMessage, currentUser} = props;

    const shouldDisableButtons = (BillStatus.OPEN != data.rowData.billStatus)
        || (currentUser && currentUser.userConfig.mandateId !== data.rowData.mandateId);

    const editIconName = shouldDisableButtons ? 'info circle' : 'edit outline';

    if (treatmentId) {
      return (
          <StyledNumberCell>
            {treatmentId}
            <Icon
                name={editIconName}
                title={getMessage("bill.editTreatment.icon.title")}
                onClick={() => {
                  if (shouldDisableButtons) {
                    openEditTreatmentModal(treatmentId, TreatmentMode.DETAILS)
                  } else {
                    openEditTreatmentModal(treatmentId)
                  }
                }}
                className="edit-icon"
            />
          </StyledNumberCell>
      );
    }

    return "";
  };

  const patientIdCellRenderer = (data: any) => {
    const patientId = data.cellData;
    const {getMessage} = props;

    if (patientId) {
      return (
          <StyledNumberCell>
              {patientId}
            <Icon
                name="edit outline"
                title={getMessage("msgEdit")}
                onClick={() => openEditPatientModal(patientId)}
                className="edit-icon"
            />
          </StyledNumberCell>
      );
    }

    return "";
  }

  const patientExternalIdCellRenderer = (data: any) => {
    const patientExtId = data.cellData;

    if (patientExtId) {
      return (
          <StyledNumberCell>
            {patientExtId}
          </StyledNumberCell>
      );
    }

    return "";
  }

  const patientInfoRender = (data: any) => {
    const rowData = data.rowData;
    const patientName = rowData.patientName;
    const lastName = rowData.patientFirstName;
    const birthDate = rowData.patientBirthDate;
    const city = rowData.patientCity;

    return <span>{`${patientName} ${lastName}, ${birthDate}, ${city}`}</span>;
  }

  const guarantorCellRenderer = (data: any) => {
    const rowData = data.rowData;
    const guarantorId = rowData.guarantorId;
    const guarantorDescription = rowData.guarantorDescription;
    const guarantorZip = rowData.guarantorZip;
    const guarantorCity = rowData.guarantorCity;
    const {getMessage} = props;

    if (guarantorId) {
      return (
          <div>
            <Icon
                name="edit outline"
                title={getMessage("msgEdit")}
                onClick={() => openEditGuarantorModal(guarantorId)}
                className="edit-guarantor-icon"
            />
            {`${guarantorDescription}, ${guarantorCity}, ${guarantorZip}`}
          </div>
      );
    }

    return "";
  }

  const treatmentsRowGetter = (treatments: Array<TreatmentOverviewDto>) => ({index}: any) => {

    if (treatments[index]) {
      Object.assign(treatments[index], {index: index + 1});

      return treatments[index];
    }

    return {
      index: index + 1
    }
  };

  const treatmentsRowRenderer = () =>
      ({className, columns, index, key, style}: any) => {

        const a11yProps = {"aria-rowindex": index + 1};

        let styles = {...style, userSelect: "none"};

        if (selectedTreatmentIx === index) {
          styles = {
            ...styles,
            ...activeRowStyle
          }
        }

        return (
            <div
                {...a11yProps}
                className={className}
                key={key}
                role="row"
                style={styles}
                onClick={() => highlightTreatment(treatments, index)}
                onDoubleClick={() => noop()}
            >
              {columns}
            </div>
        );
      };

  const highlightConsultation = (index: number) => {
    setSelectedConsultationIx(index);
  };

  const consultationDateTimeCellRenderer = (data: any) => {
    const date = moment(data.rowData.consultationDate).format("DD.MM.YYYY");
    const time = moment(data.rowData.consultationTime, "hh:mm:ss").format(moment.HTML5_FMT.TIME);
    return `${date} - ${time}`;
  };

  const consultationsRowGetter = (consultations: Array<TreatmentOverviewConsultationDto>) => ({index}: any) => {

    if (consultations[index]) {
      Object.assign(consultations[index], {index: index + 1});

      return consultations[index];
    }

    return {
      index: index + 1
    }
  };

  const consultationsRowRenderer = () => ({className, columns, index, key, style}: any) => {
    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    if (selectedConsultationIx === index) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => highlightConsultation(index)}
            onDoubleClick={() => noop()}
        >
          {columns}
        </div>
    );
  };

  const renderConsultationsTable = (consultations: Array<TreatmentOverviewConsultationDto>): React.ReactNode => {

    return (
        <StyledTable
            rowGetter={consultationsRowGetter(consultations)}
            rowRenderer={consultationsRowRenderer()}
            rowCount={consultations.length}
            columns={[
              {
                width: 300,
                label: `${getMessage("date")} - ${getMessage("time")}`,
                dataKey: "date",
                cellRenderer: (data: any) => consultationDateTimeCellRenderer(data)
              },
              {
                width: 150,
                label: getMessage("value"),
                dataKey: "consultationAmount",
                cellRenderer: consultationAmountCellRenderer
              }
            ]}
        />
    )
  };

  const renderTreatmentsTable = (treatments: Array<TreatmentOverviewDto>): React.ReactNode => {


    return (
        <StyledTreatmentsTable
            rowGetter={treatmentsRowGetter(treatments)}
            rowRenderer={treatmentsRowRenderer()}
            rowCount={treatments.length}
            columns={[
              {
                width: 70,
                label: "",
                dataKey: "treatmentStatus",
                flexShrink: 0,
                cellRenderer: treatmentCellRenderer(treatments)
              },
              {
                width: 100,
                label: getMessage("id"),
                dataKey: "id",
                flexShrink: 0,
                cellRenderer: treatmentIdCellRenderer
              },
              {
                width: 170,
                label: getMessage("date"),
                dataKey: "treatmentDate",
                cellRenderer: treatmentDateCellRenderer
              },
              {
                width: 150,
                label: getMessage("tariff"),
                dataKey: "tariffDescription"
              },
              {
                width: 200,
                label: getMessage("treatmentOverview.bookingIdProduct"),
                dataKey: "interventionBookingId",
                cellRenderer: treatmentBookingIdProductCellRenderer
              },
              {
                width: 120,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: amountCellRenderer
              },
              {
                width: 100,
                label: getMessage("treatmentOverview.patientId"),
                dataKey: "patientId",
                cellRenderer: patientIdCellRenderer
              },
              {
                width: 75,
                label: getMessage("treatmentOverview.patientExternalId"),
                dataKey: "patientExternalIdManual",
                cellRenderer: patientExternalIdCellRenderer
              },
              {
                width: 75,
                label: getMessage("patient.patientExternalId"),
                dataKey: "patientExternalId",
                cellRenderer: patientExternalIdCellRenderer
              },
              {
                width: 300,
                label: getMessage("patient"),
                dataKey: "patientId",
                cellRenderer: patientInfoRender
              },
              {
                width: 350,
                label: getMessage("guarantor"),
                dataKey: "guarantorDescription",
                cellRenderer: guarantorCellRenderer
              }
            ]}
        />
    )
  };

  const renderUpsertPatientForm = (patientId: number | undefined) => {
    return editPatientModalOpen && patientId
        ? <UpsertPatient
            modalOpen={editPatientModalOpen}
            toggleModal={toggleEditPatientModal}
            countries={countries}
            languages={languages}
            patient={patient}
            patientId={patientId}
            onPatientSave={onPatientSave}
            currentMandates={currentMandates}
            getAllMandates={getAllMandates}
        />
        : ""
  }

  const renderUpsertTreatmentForm = () => {
    return editTreatmentModalOpen &&
        <TreatmentsForm
            treatmentFormOpen={editTreatmentModalOpen}
            toggleUpsertModal={toggleEditTreatmentModal}
            treatment={treatment}
            currentPatientId={treatment ? treatment.patientId : undefined}
            currentConsultations={currentConsultations}
        />
  }

  const renderUpsertGuarantorModal = (guarantorId: number): React.ReactNode => {
    return editGuarantorModalOpen && guarantorId && guarantor
        ? <Modal
            open={editGuarantorModalOpen}
            onClose={hideGuarantorModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("guarantors")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertGuarantorForm
                guarantor={guarantor}
                guarantorId={guarantorId}
                onCancel={hideGuarantorModal}
                onSave={onGuarantorSave}
            />
          </FormModalContentContainer>
        </Modal>
        : ""
  };

  const numOfTreatments = treatments.length;
  const header = numOfTreatments > 0
      ? getMessageWithNamedParams("treatments.numOfTreatments", {numOfTreatments: numOfTreatments})
      : getMessage("treatments")
  return (
      <StyledForm>
        <FormContainer>
          <FormContent>
            <StyledFormContent>
              <StyledContainer style={{width: "35%"}}>
                <Header>
                  {header}
                </Header>
                <TableContainer id="treatmentsMainTable">
                  {treatmentsLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("treatments").toLowerCase()}`}/>
                      : renderTreatmentsTable(treatments)}
                </TableContainer>
              </StyledContainer>
              <StyledContainer style={{width: "25%", maxWidth: "335px"}} className="margin">
                <Header>
                  {getMessage("consultations")}
                </Header>
                <TableContainer>
                  {treatments[selectedTreatmentIx] && treatments[selectedTreatmentIx].consultations.length > 0
                      ? renderConsultationsTable(treatments[selectedTreatmentIx].consultations)
                      : renderConsultationsTable([])
                  }
                </TableContainer>
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
        </FormContainer>
        {!!(treatments.length && treatments[selectedTreatmentIx] && treatments[selectedTreatmentIx].patientId)
            && renderUpsertPatientForm(treatments[selectedTreatmentIx].patientId)
        }
        {renderUpsertTreatmentForm()}
        {!!(treatments.length && treatments[selectedTreatmentIx] && treatments[selectedTreatmentIx].guarantorId) &&
            renderUpsertGuarantorModal(treatments[selectedTreatmentIx].guarantorId)
        }
      </StyledForm>
  );
};

export default withAuthContext(
    withRouterWorkaround(
        withMessages(
            withConsultationsContext(
                withMandateContext(
                    TreatmentOverviewTablesSegment)))));
